// Cue numbers: grammar, comparator, entry normalization and the suggested next number
// (docs/format/README.md §7).

export const CUE_NUMBER_PATTERN = /^(?:[0-9]{1,4}(?:\.[0-9]{1,3})?[A-Z]?|[A-Z]{1,3})$/;
const NUMERIC = /^([0-9]{1,4})(?:\.([0-9]{1,3}))?([A-Z]?)$/;

type Parsed =
  | { kind: "numeric"; milli: number; suffix: string }
  | { kind: "letters"; letters: string }
  | { kind: "invalid"; raw: string };

function parse(n: string): Parsed {
  const m = NUMERIC.exec(n);
  if (m) return { kind: "numeric", milli: Number(m[1]) * 1000 + Number((m[2] ?? "").padEnd(3, "0")), suffix: m[3] ?? "" };
  if (/^[A-Z]{1,3}$/.test(n)) return { kind: "letters", letters: n };
  return { kind: "invalid", raw: n };
}

const RANK = { numeric: 0, letters: 1, invalid: 2 } as const;

/** Total order on cue numbers. `5.1` and `5.10` compare equal. */
export function compareCueNumbers(a: string, b: string): number {
  const pa = parse(a);
  const pb = parse(b);
  if (pa.kind !== pb.kind) return RANK[pa.kind] - RANK[pb.kind];
  if (pa.kind === "numeric" && pb.kind === "numeric") {
    if (pa.milli !== pb.milli) return pa.milli - pb.milli;
    return pa.suffix === pb.suffix ? 0 : pa.suffix < pb.suffix ? -1 : 1;
  }
  if (pa.kind === "letters" && pb.kind === "letters") {
    if (pa.letters.length !== pb.letters.length) return pa.letters.length - pb.letters.length;
    return pa.letters === pb.letters ? 0 : pa.letters < pb.letters ? -1 : 1;
  }
  return a === b ? 0 : a < b ? -1 : 1;
}

export const isValidCueNumber = (n: string): boolean => CUE_NUMBER_PATTERN.test(n);

/** What the UI stores for typed input: trimmed, letters uppercased, leading zeros stripped. */
export function normalizeCueNumber(input: string): string {
  const s = input.trim().toUpperCase();
  const m = /^0*([0-9]+)(\.[0-9]*)?([A-Z]?)$/.exec(s);
  if (!m) return s;
  const whole = m[1] ?? "0";
  return `${whole}${m[2] ?? ""}${m[3] ?? ""}`;
}

function format(milli: number): string {
  const whole = Math.floor(milli / 1000);
  const frac = String(milli % 1000).padStart(3, "0").replace(/0+$/, "");
  return frac ? `${whole}.${frac}` : String(whole);
}

/**
 * A prefilled number for a new cue, from the department's cues just before and after it in show order.
 * At the end: previous whole number + 1. Between two cues: the shortest decimal strictly between them,
 * nearest their midpoint (`.5` when it fits; ties go toward the middle of the whole number).
 * Returns "" when nothing fits.
 */
export function suggestCueNumber(prev: string | null, next: string | null): string {
  const p = prev === null ? { kind: "numeric" as const, milli: 0, suffix: "" } : parse(prev);
  if (p.kind !== "numeric") return "";
  const n = next === null ? null : parse(next);
  if (n === null || n.kind !== "numeric") {
    const whole = Math.floor(p.milli / 1000) + 1;
    return whole > 9999 ? "" : String(whole);
  }
  const lo = p.milli;
  const hi = n.milli;
  const nextWhole = Math.floor(lo / 1000) * 1000 + 1000;
  if (nextWhole < hi) return format(nextWhole);
  for (const step of [100, 10, 1]) {
    const first = Math.floor(lo / step) * step + step;
    if (first >= hi) continue;
    const mid = (lo + hi) / 2;
    const half = Math.floor(lo / 1000) * 1000 + 500;
    let best = first;
    for (let v = first; v < hi; v += step) {
      const d = Math.abs(v - mid) - Math.abs(best - mid);
      if (d < 0 || (d === 0 && Math.abs(v - half) < Math.abs(best - half))) best = v;
    }
    return format(best);
  }
  return "";
}
